import { useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useKeyboardShortcuts } from '@/context/KeyboardShortcutsContext';
import { useSidebar } from '@/components/ui/sidebar';
import { useDashboardTab } from '@/context/DashboardTabContext';
import { useAccountSettings } from '@/context/AccountSettingsContext';
import { useBusinessContext } from '@/context/BusinessContext';
import { useBusinessSettings, BusinessSettingsSection } from '@/context/BusinessSettingsContext';
import { useNavigateToTab } from '@/lib/navigation';
import { businessSettingsPath } from '@/lib/settings-navigation';
import {
  BusinessTabs, EmployeeTabs,
  BUSINESS_TAB_ORDER, EMPLOYEE_TAB_ORDER, FRANCHISE_TAB_ORDER,
  isTabAllowedForPlan,
  canOpenPortfolio,
  type DashboardType,
} from '@/lib/constants/dashboard';
import { useSubscription } from '@/context/SubscriptionContext';
import { toast } from '@/hooks/ui/use-toast';
import {
  LayoutDashboard,
  Users,
  Star,
  Settings,
  Settings2,
  PanelLeft,
  CreditCard,
  Gift,
  BarChart3,
  Clock,
  Building2,
  Briefcase,
  ArrowRight,
  ArrowLeft,
  ArrowUp,
  ArrowDown,
  Bot,
  Store,
  Mail,
} from 'lucide-react';

// Dashboard routes in order for Ctrl+Left/Right navigation
// 'business-select' is a virtual stop that opens the business selector dropdown
const DASHBOARDS = ['/portfolio', 'business-select', '/dashboard', '/employee-dashboard'] as const;

// Map current tab to appropriate business settings section
const TAB_TO_SETTINGS_SECTION: Record<string, BusinessSettingsSection> = {
  'time-tracking': 'staff',
  'shift-management': 'staff',
  'payroll': 'staff',
  'employee-list': 'staff',
  'communications': 'business',
  'reviews': 'reviews',
  'insights': 'business',
  'forms': 'business',
  'google': 'integrations',
  'feedback-intelligence': 'reviews',
  'customers': 'business',
  'contacts': 'business',
  'contacts-tags': 'business',
  'contacts-busyness': 'business',
  'loyalty': 'business',
  'wallet-statistics': 'business',
  'coupons': 'business',
  'bulk-mail': 'integrations',
  'email-templates': 'integrations',
  'social-accounts': 'integrations',
  'social-posts': 'integrations',
  'social-inbox': 'integrations',
  'social-analytics': 'integrations',
  'social-auto-reply': 'integrations',
  'signage': 'integrations',
  'overview': 'business',
  'schedule': 'staff',
  'analytics': 'business',
  'alerts': 'business',
};

export function DefaultCommands() {
  const { registerCommand } = useKeyboardShortcuts();
  const navigate = useNavigate();
  const location = useLocation();
  const { toggleSidebar, aiModeActive, setAiModeActive, state, displayMode, setDisplayMode } = useSidebar();
  const { businesses, selectedBusinessId, switchBusiness } = useBusinessContext();
  const {
    businessDashboardTab,
    employeeDashboardTab,
    franchiseDashboardTab,
    setBusinessDashboardTab,
    setEmployeeDashboardTab,
    setFranchiseDashboardTab,
  } = useDashboardTab();
  const { openAccountSettings } = useAccountSettings();
  const { openBusinessSettings } = useBusinessSettings();
  const navigateToTab = useNavigateToTab();
  const { currentPlan } = useSubscription();
  const planName = currentPlan?.name;

  // Determine current dashboard index
  // Note: 'business-select' (index 1) is a virtual stop, so /dashboard maps to index 2
  const getCurrentDashboardIndex = useCallback(() => {
    const path = location.pathname;
    if (path.startsWith('/portfolio')) return 0;
    if (path.startsWith('/dashboard')) return 2;
    if (path.startsWith('/employee-dashboard')) return 3;
    return -1;
  }, [location.pathname]);

  // Get current tab info based on dashboard
  const getCurrentTabInfo = useCallback(() => {
    const visible = (dashboard: DashboardType, tabs: readonly string[]) =>
      tabs.filter((tab) => isTabAllowedForPlan(planName, dashboard, tab));
    const dashboardIndex = getCurrentDashboardIndex();
    switch (dashboardIndex) {
      case 0:
        return { tabs: visible('franchise', FRANCHISE_TAB_ORDER), currentTab: franchiseDashboardTab, setTab: setFranchiseDashboardTab };
      case 2:
        return { tabs: visible('business', BUSINESS_TAB_ORDER), currentTab: businessDashboardTab, setTab: setBusinessDashboardTab };
      case 3:
        return { tabs: visible('employee', EMPLOYEE_TAB_ORDER), currentTab: employeeDashboardTab, setTab: setEmployeeDashboardTab };
      default:
        return null;
    }
  }, [getCurrentDashboardIndex, businessDashboardTab, employeeDashboardTab, franchiseDashboardTab, setBusinessDashboardTab, setEmployeeDashboardTab, setFranchiseDashboardTab, planName]);

  // Check if the business selector dropdown is currently open
  const isBusinessSelectOpen = useCallback(() => {
    const trigger = document.querySelector('[data-onboarding-id="business-selector"]') as HTMLElement;
    return trigger?.getAttribute('aria-expanded') === 'true';
  }, []);

  // Close the business selector if it's open
  const closeBusinessSelect = useCallback(() => {
    if (isBusinessSelectOpen()) {
      const trigger = document.querySelector('[data-onboarding-id="business-selector"]') as HTMLElement;
      trigger?.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }));
    }
  }, [isBusinessSelectOpen]);

  // Navigate to next/previous dashboard (non-cycleable: clamped to bounds)
  const navigateDashboard = useCallback((direction: 'next' | 'prev') => {
    // If the business selector is open, treat current position as the virtual stop (index 1)
    const currentIndex = isBusinessSelectOpen() ? 1 : getCurrentDashboardIndex();

    if (currentIndex === -1) {
      navigate('/dashboard');
      return;
    }

    let newIndex: number;
    if (direction === 'next') {
      newIndex = Math.min(currentIndex + 1, DASHBOARDS.length - 1);
    } else {
      newIndex = Math.max(currentIndex - 1, 0);
    }

    // Already at the edge, do nothing
    if (newIndex === currentIndex) return;

    // Close the business selector first if it's open
    closeBusinessSelect();

    const target = DASHBOARDS[newIndex];
    if (target === '/portfolio' && !canOpenPortfolio(planName, businesses.length)) return;
    if (target === 'business-select') {
      // Virtual stop: ensure we're on /dashboard, then open the business selector
      if (!location.pathname.startsWith('/dashboard')) {
        navigate('/dashboard');
      }
      requestAnimationFrame(() => {
        const trigger = document.querySelector('[data-onboarding-id="business-selector"]') as HTMLElement;
        trigger?.click();
      });
    } else {
      navigate(target);
    }
  }, [getCurrentDashboardIndex, navigate, location.pathname, closeBusinessSelect, isBusinessSelectOpen, planName, businesses.length]);

  // Toggle AI chat panel
  const toggleAI = useCallback(() => {
    if (planName === 'free') {
      toast.error('AI is not included on the free plan');
      return;
    }
    if (!aiModeActive) {
      // Opening AI - expand sidebar if collapsed
      if (state === 'collapsed' || displayMode === 'always-collapsed') {
        setDisplayMode('always-expanded');
      }
      setAiModeActive(true);
    } else {
      setAiModeActive(false);
    }
  }, [aiModeActive, state, displayMode, setDisplayMode, setAiModeActive, planName]);

  // Switch to next/previous business
  const cycleBusiness = useCallback((direction: 'next' | 'prev') => {
    if (businesses.length <= 1) return;

    const currentIndex = businesses.findIndex(b => b.id === selectedBusinessId);
    let newIndex: number;

    if (currentIndex === -1) {
      newIndex = 0;
    } else if (direction === 'next') {
      newIndex = (currentIndex + 1) % businesses.length;
    } else {
      newIndex = (currentIndex - 1 + businesses.length) % businesses.length;
    }

    const nextId = businesses[newIndex].id;
    switchBusiness(nextId);
    const settingsPath = businessSettingsPath(location.pathname, location.search, nextId);
    if (settingsPath) navigate(settingsPath);
  }, [businesses, selectedBusinessId, switchBusiness, location.pathname, location.search, navigate]);

  // Navigate to next/previous tab
  const navigateTab = useCallback((direction: 'next' | 'prev') => {
    const tabInfo = getCurrentTabInfo();
    if (!tabInfo) return;

    const { tabs, currentTab, setTab } = tabInfo;
    const currentIndex = tabs.indexOf(currentTab);

    let newIndex: number;
    if (currentIndex === -1) {
      newIndex = 0;
    } else if (direction === 'next') {
      newIndex = (currentIndex + 1) % tabs.length;
    } else {
      newIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    }
    setTab(tabs[newIndex]);
  }, [getCurrentTabInfo]);

  // Get the current tab name for business settings section mapping
  const getCurrentTab = useCallback(() => {
    const tabInfo = getCurrentTabInfo();
    return tabInfo?.currentTab || null;
  }, [getCurrentTabInfo]);

  // Open business settings with context-aware section
  const handleOpenBusinessSettings = useCallback(() => {
    const currentTab = getCurrentTab();
    const section = currentTab ? TAB_TO_SETTINGS_SECTION[currentTab] : undefined;
    openBusinessSettings(section || 'business');
  }, [getCurrentTab, openBusinessSettings]);

  useEffect(() => {
    const unregisters: (() => void)[] = [];

    // Arrow key navigation
    unregisters.push(
      registerCommand({
        id: 'nav-dashboard-next',
        keys: { key: 'ArrowRight', modifiers: ['ctrl'] },
        label: 'Next Dashboard',
        description: 'Cycle to next dashboard',
        action: () => navigateDashboard('next'),
        category: 'navigation',
        icon: ArrowRight,
        keywords: ['arrow', 'switch', 'cycle'],
      })
    );

    unregisters.push(
      registerCommand({
        id: 'nav-dashboard-prev',
        keys: { key: 'ArrowLeft', modifiers: ['ctrl'] },
        label: 'Previous Dashboard',
        description: 'Cycle to previous dashboard',
        action: () => navigateDashboard('prev'),
        category: 'navigation',
        icon: ArrowLeft,
        keywords: ['arrow', 'switch', 'cycle'],
      })
    );

    unregisters.push(
      registerCommand({
        id: 'nav-tab-next',
        keys: { key: 'ArrowDown', modifiers: ['ctrl'] },
        label: 'Next Tab',
        description: 'Cycle to next tab',
        action: () => navigateTab('next'),
        category: 'navigation',
        icon: ArrowDown,
        keywords: ['arrow', 'switch', 'cycle'],
      })
    );

    unregisters.push(
      registerCommand({
        id: 'nav-tab-prev',
        keys: { key: 'ArrowUp', modifiers: ['ctrl'] },
        label: 'Previous Tab',
        description: 'Cycle to previous tab',
        action: () => navigateTab('prev'),
        category: 'navigation',
        icon: ArrowUp,
        keywords: ['arrow', 'switch', 'cycle'],
      })
    );

    // Main navigation commands (shown in palette)
    unregisters.push(
      registerCommand({
        id: 'nav-business-dashboard',
        label: 'Business Dashboard',
        description: 'Main business dashboard',
        action: () => navigate('/dashboard'),
        category: 'navigation',
        icon: LayoutDashboard,
        keywords: ['home', 'main', 'business', 'overview'],
        context: {
          dashboards: ['employee', 'franchise'],
        },
      })
    );

    unregisters.push(
      registerCommand({
        id: 'nav-employee-dashboard',
        label: 'Employee Dashboard',
        description: 'Employee management',
        action: () => navigate('/employee-dashboard'),
        category: 'navigation',
        icon: Users,
        keywords: ['staff', 'team', 'workers', 'employees'],
        context: {
          dashboards: ['business', 'franchise'],
        },
      })
    );

    if (canOpenPortfolio(planName, businesses.length)) unregisters.push(
      registerCommand({
        id: 'nav-portfolio',
        label: 'Portfolio',
        description: 'Franchise portfolio',
        action: () => navigate('/portfolio'),
        category: 'navigation',
        icon: Building2,
        keywords: ['franchise', 'multi', 'businesses', 'overview'],
        context: {
          dashboards: ['business', 'employee'],
        },
      })
    );

    // Business dashboard tabs
    unregisters.push(
      registerCommand({
        id: 'nav-reviews',
        label: 'Reviews',
        description: 'Customer reviews',
        action: () => navigateToTab('business', BusinessTabs.REVIEWS),
        category: 'navigation',
        icon: Star,
        keywords: ['feedback', 'ratings', 'google', 'stars'],
        context: {
          dashboards: ['business'],
          tabs: ['reviews', 'insights', 'forms', 'google', 'feedback-intelligence'],
        },
      })
    );

    if (isTabAllowedForPlan(planName, 'business', BusinessTabs.INSIGHTS)) unregisters.push(
      registerCommand({
        id: 'nav-insights',
        label: 'Insights',
        description: 'Review analytics',
        action: () => navigateToTab('business', BusinessTabs.INSIGHTS),
        category: 'navigation',
        icon: BarChart3,
        keywords: ['analytics', 'statistics', 'charts'],
        context: {
          dashboards: ['business'],
          tabs: ['reviews', 'insights', 'forms', 'google', 'feedback-intelligence'],
        },
      })
    );

    unregisters.push(
      registerCommand({
        id: 'nav-wallet-passes',
        label: 'Wallet Passes',
        description: 'Apple & Google Wallet passes',
        action: () => navigateToTab('business', BusinessTabs.LOYALTY),
        category: 'navigation',
        icon: CreditCard,
        keywords: ['loyalty', 'coupons', 'passes', 'wallet', 'apple', 'google', 'rewards', 'offers'],
        context: {
          dashboards: ['business'],
          tabs: ['contacts', 'loyalty', 'coupons', 'wallet-statistics'],
        },
      })
    );

    unregisters.push(
      registerCommand({
        id: 'nav-loyalty',
        label: 'Loyalty Cards',
        description: 'Digital loyalty cards and rewards',
        action: () => navigateToTab('business', BusinessTabs.LOYALTY),
        category: 'navigation',
        icon: Gift,
        keywords: ['rewards', 'passes', 'wallet', 'apple', 'google', 'points', 'stamps'],
        context: {
          dashboards: ['business'],
          tabs: ['contacts', 'loyalty', 'coupons', 'wallet-statistics'],
        },
      })
    );

    if (isTabAllowedForPlan(planName, 'business', BusinessTabs.CONTACTS)) unregisters.push(
      registerCommand({
        id: 'nav-contacts',
        label: 'Contacts',
        description: 'Contact management',
        action: () => navigateToTab('business', BusinessTabs.CONTACTS),
        category: 'navigation',
        icon: Briefcase,
        keywords: ['crm', 'customers', 'clients', 'people'],
        context: {
          dashboards: ['business'],
          tabs: ['contacts', 'contacts-tags', 'contacts-busyness'],
        },
      })
    );

    if (isTabAllowedForPlan(planName, 'business', BusinessTabs.CONTACTS_TAGS)) unregisters.push(
      registerCommand({
        id: 'nav-contacts-tags',
        label: 'Contact Tags',
        description: 'Manage contact tags',
        action: () => navigateToTab('business', BusinessTabs.CONTACTS_TAGS),
        category: 'navigation',
        icon: Briefcase,
        keywords: ['tags', 'labels', 'categories', 'organize'],
        context: {
          dashboards: ['business'],
          tabs: ['contacts', 'contacts-tags', 'contacts-busyness'],
        },
      })
    );

    if (isTabAllowedForPlan(planName, 'business', BusinessTabs.CONTACTS_BUSYNESS)) unregisters.push(
      registerCommand({
        id: 'nav-contacts-busyness',
        label: 'Busyness Analytics',
        description: 'Customer traffic analytics',
        action: () => navigateToTab('business', BusinessTabs.CONTACTS_BUSYNESS),
        category: 'navigation',
        icon: BarChart3,
        keywords: ['analytics', 'traffic', 'busy', 'popular times'],
        context: {
          dashboards: ['business'],
          tabs: ['contacts', 'contacts-tags', 'contacts-busyness'],
        },
      })
    );

    unregisters.push(
      registerCommand({
        id: 'nav-coupons',
        label: 'Coupons',
        description: 'One-time coupon offers',
        action: () => navigateToTab('business', BusinessTabs.COUPONS),
        category: 'navigation',
        icon: Gift,
        keywords: ['offers', 'discounts', 'tokens', 'deals'],
        context: {
          dashboards: ['business'],
          tabs: ['contacts', 'loyalty', 'coupons', 'wallet-statistics'],
        },
      })
    );

    unregisters.push(
      registerCommand({
        id: 'nav-wallet-statistics',
        label: 'Wallet Analytics',
        description: 'Unified wallet pass analytics',
        action: () => navigateToTab('business', BusinessTabs.WALLET_STATISTICS),
        category: 'navigation',
        icon: BarChart3,
        keywords: ['analytics', 'statistics', 'loyalty', 'coupons', 'dashboard', 'overview'],
        context: {
          dashboards: ['business'],
          tabs: ['contacts', 'loyalty', 'coupons', 'wallet-statistics'],
        },
      })
    );

    // Email commands
    if (isTabAllowedForPlan(planName, 'business', BusinessTabs.BULK_MAIL)) unregisters.push(
      registerCommand({
        id: 'nav-email',
        label: 'Email',
        description: 'Email marketing',
        action: () => navigateToTab('business', BusinessTabs.BULK_MAIL),
        category: 'navigation',
        icon: Mail,
        keywords: ['mail', 'newsletter', 'campaign', 'templates'],
        context: {
          dashboards: ['business'],
          tabs: ['bulk-mail', 'email-templates'],
        },
      })
    );

    if (isTabAllowedForPlan(planName, 'business', BusinessTabs.BULK_MAIL)) unregisters.push(
      registerCommand({
        id: 'nav-bulk-mail',
        label: 'Bulk Mail',
        description: 'Send bulk emails',
        action: () => navigateToTab('business', BusinessTabs.BULK_MAIL),
        category: 'navigation',
        icon: Mail,
        keywords: ['newsletter', 'campaign', 'mass', 'send'],
        context: {
          dashboards: ['business'],
          tabs: ['bulk-mail', 'email-templates'],
        },
      })
    );

    if (isTabAllowedForPlan(planName, 'business', BusinessTabs.EMAIL_TEMPLATES)) unregisters.push(
      registerCommand({
        id: 'nav-email-templates',
        label: 'Email Templates',
        description: 'Manage email templates',
        action: () => navigateToTab('business', BusinessTabs.EMAIL_TEMPLATES),
        category: 'navigation',
        icon: Mail,
        keywords: ['template', 'email', 'design'],
        context: {
          dashboards: ['business'],
          tabs: ['bulk-mail', 'email-templates'],
        },
      })
    );

    unregisters.push(
      registerCommand({
        id: 'nav-time-tracking',
        label: 'Time Tracking',
        description: 'Employee hours',
        action: () => navigateToTab('employee', EmployeeTabs.TIME_TRACKING),
        category: 'navigation',
        icon: Clock,
        keywords: ['hours', 'attendance', 'clock'],
        context: {
          dashboards: ['employee'],
          tabs: ['time-tracking', 'payroll', 'insights'],
        },
      })
    );

    unregisters.push(
      registerCommand({
        id: 'nav-shift-management',
        label: 'Shift Management',
        description: 'Schedule shifts',
        action: () => navigateToTab('employee', EmployeeTabs.SHIFT_MANAGEMENT),
        category: 'navigation',
        icon: Clock,
        keywords: ['schedule', 'planning', 'roster', 'calendar'],
        context: {
          dashboards: ['employee'],
          tabs: ['shift-management', 'employee-list', 'insights'],
        },
      })
    );

    // Settings (opens sidebar panel, not a page)
    unregisters.push(
      registerCommand({
        id: 'open-settings',
        label: 'Account Settings',
        description: 'Personal account settings',
        keys: { key: ',', modifiers: ['ctrl'] },
        action: () => openAccountSettings(),
        category: 'settings',
        icon: Settings,
        keywords: ['preferences', 'configuration', 'options', 'profile', 'account', 'personal'],
      })
    );

    unregisters.push(
      registerCommand({
        id: 'open-business-settings',
        label: 'Business Settings',
        description: 'Business configuration',
        keys: { key: '.', modifiers: ['ctrl'] },
        action: handleOpenBusinessSettings,
        category: 'settings',
        icon: Settings2,
        keywords: ['business', 'settings', 'configuration', 'venue', 'hours', 'integrations'],
      })
    );

    // Action commands
    unregisters.push(
      registerCommand({
        id: 'toggle-sidebar',
        label: 'Toggle Sidebar',
        description: 'Show/hide sidebar',
        keys: { key: 'b', modifiers: ['ctrl'] },
        action: toggleSidebar,
        category: 'actions',
        icon: PanelLeft,
        keywords: ['menu', 'navigation', 'panel', 'collapse'],
      })
    );

    if (planName !== 'free') unregisters.push(
      registerCommand({
        id: 'toggle-ai-chat',
        label: 'Toggle AI Assistant',
        description: 'Open/close AI chat',
        keys: { key: ' ', modifiers: ['ctrl'] },
        action: toggleAI,
        category: 'actions',
        icon: Bot,
        keywords: ['ai', 'chat', 'assistant', 'bot', 'help'],
      })
    );

    // Business switching
    unregisters.push(
      registerCommand({
        id: 'switch-business-next',
        label: 'Next Business',
        description: 'Switch to next business',
        keys: { key: ']', modifiers: ['ctrl'] },
        action: () => cycleBusiness('next'),
        category: 'actions',
        icon: Store,
        keywords: ['business', 'switch', 'store', 'venue'],
      })
    );

    unregisters.push(
      registerCommand({
        id: 'switch-business-prev',
        label: 'Previous Business',
        description: 'Switch to previous business',
        keys: { key: '[', modifiers: ['ctrl'] },
        action: () => cycleBusiness('prev'),
        category: 'actions',
        icon: Store,
        keywords: ['business', 'switch', 'store', 'venue'],
      })
    );

    return () => unregisters.forEach((fn) => fn());
  }, [
    registerCommand,
    navigate,
    toggleSidebar,
    toggleAI,
    cycleBusiness,
    navigateToTab,
    openAccountSettings,
    handleOpenBusinessSettings,
    navigateDashboard,
    navigateTab,
    planName,
    businesses.length,
  ]);

  return null;
}
